import * as React from "react"
import { useTranslation } from "react-i18next"
import { useMutation } from "@tanstack/react-query"
import { CheckIcon, CopyIcon, LinkIcon, QrCodeIcon } from "lucide-react"
import { toast } from "sonner"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/features/auth/auth-context"
import { FeatureOff } from "@/components/FeatureOff"
import { api, isApiError, requestBlob } from "@/lib/api"
import { useFormatDay } from "@/lib/dates"
import { endpoints } from "@/lib/endpoints"
import type { ApprovalLinkIssued } from "@steelbill/types"

/**
 * §17.1's owner-facing half — the customer link, and "record as sent".
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ITS OWN FILE RATHER THAN ANOTHER BLOCK IN `QuotationDetailScreen.tsx`, WHICH
 * IS ALREADY 1,700 LINES. The detail screen gains one import and one line; every
 * decision about the approval loop is here, where somebody looking for it will
 * find it. It is the same split `StatusCard` and `QuotationTotalsCard` already
 * use, applied to a feature rather than to a panel.
 *
 * WHAT IT OFFERS, AND IT IS EXACTLY WHAT THE OWNER ASKED FOR:
 *
 *  1. THE LINK, copied to the clipboard, to paste into WhatsApp. There is
 *     deliberately no "send on WhatsApp for me" button that opens `wa.me` with a
 *     pre-filled message: the existing Share item on this screen already does
 *     the platform share, and a second path would be a second idea of what
 *     message the customer receives.
 *  2. THE QR IS NOT DRAWN HERE. It is printed on the SHEET, bottom right, by the
 *     server (`approvalQrBlock` in services/pdf/quotation-pdf.ts) — because the
 *     door the owner asked for is a scan off PAPER, not off a screen. Drawing a
 *     second QR here would be a second encoder of the same credential, and the
 *     two would eventually differ. The card says where it is instead.
 *  3. RECORD AS SENT, for the call and the counter — the case the app cannot
 *     observe, and the reason the register fills with permanent DRAFTs.
 *
 * GATED ON `quotation.share` THROUGH `can()` — the SAME string the routes
 * require (§2.1), resolved through the same map in packages/types, so the button
 * and the API cannot disagree. It HIDES rather than greys out, for the reason
 * the email item on the detail screen states: a disabled control invites the
 * question of how to enable it, and the honest answer for somebody without the
 * capability is that it is not theirs to do.
 *
 * NOT GATED ON THE `approval_links` FLAG, because there is no `useFlag` on this
 * client and this screen does not invent one. With the flag off the routes
 * answer 404 (§18.2) and the card shows the API's own refusal inline — which is
 * the same design `QuotationDetailScreen` already chose for the `mail` flag.
 * ═════════════════════════════════════════════════════════════════════════════
 */

export interface QuotationApprovalCardProps {
  quotationId: number
  /** Set once the offer has gone out, by any door. From the document payload. */
  sentAt: string | null
  /**
   * HOW it went out, and the reason the two "record as sent" buttons could be
   * deleted at all.
   *
   * `sent_channel` answers a customer who says the quotation never arrived: "we
   * emailed it on the 4th" and "he says he told them on the 4th" are different
   * answers and only one can be checked. A column nothing DISPLAYS quietly
   * becomes decorative and gets deleted a year later by somebody who cannot see
   * what it was for — so with the buttons that wrote it gone, this line is what
   * keeps it alive.
   *
   * Null for a quotation stamped before the column existed, or through a path
   * that captured no channel; the screen prints the plain date then.
   */
  sentChannel: "EMAIL" | "WHATSAPP" | "LINK" | "RECORDED" | null
  /** Set once the customer has answered through the portal. */
  decidedAt: string | null
  /**
   * WHO ANSWERED, AND WHAT THEY SAID — both as the customer typed them.
   *
   * THIS CARD SHOWED NEITHER FOR A WHILE, and the gap was the feature failing at
   * its own point: the portal asks the customer for a name and offers a note box
   * whose placeholder suggests "please revise the delivery date", and the owner
   * then saw "answered on 18 Aug" with no name and no note. The one sentence
   * that decides what he does next was readable only inside `audit_log`, behind
   * `audit.view`, which he does not hold.
   */
  decidedByName: string | null
  decisionNote: string | null
}

export function QuotationApprovalCard({
  quotationId,
  sentAt,
  sentChannel,
  decidedAt,
  decidedByName,
  decisionNote,
}: QuotationApprovalCardProps): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()
  const formatDay = useFormatDay()

  const [link, setLink] = React.useState<ApprovalLinkIssued | null>(null)
  const [copied, setCopied] = React.useState(false)
  const [failure, setFailure] = React.useState<string | null>(null)
  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * THE `approval_links` FLAG IS OFF, LEARNT THE ONLY WAY THIS CLIENT CAN.
   *
   * There is no `useFlag()` here and there must not be: `GET /flags` needs
   * `settings.write`, which the OWNER does not hold (see
   * features/settings/use-flags.ts), so a hook built on it would answer 403 for
   * the very person this card is for. A flagged module is discovered exactly as
   * §18.2 intends — the API answers 404 — and the screen then renders
   * `FeatureOff`, like every other flagged surface in the app.
   *
   * WHAT THIS REPLACED. The 404's raw sentence was rendered verbatim in a
   * destructive Alert, so the owner read
   * `Not Found: POST /api/v1/quotations/15/approval-link` under three buttons
   * that did nothing. FeatureOff.tsx's own header names that failure: a control
   * that never works is how somebody concludes the app is broken rather than
   * merely incomplete — and here it printed a route path at him to say so.
   *
   * BOTH ROUTES CARRY THE FLAG (`requireFlag("approval_links")` on
   * `/:id/approval-link` AND `/:id/record-sent`, routes/quotations.ts), so one
   * flag answers for the whole card and nothing working is hidden with it. The
   * facts above — sent when, answered when — come off the DOCUMENT and stay.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const [moduleOff, setModuleOff] = React.useState(false)

  /**
   * A 404 is the flag; anything else is worth reading verbatim.
   *
   * The API's own sentence is kept for every other failure precisely because it
   * is more use than a generic one — a missing `PUBLIC_APP_BASE_URL` names the
   * variable, which is the difference between a five-second fix and an evening.
   */
  function handleFailure(error: unknown, fallback: string): void {
    if (isApiError(error) && error.status === 404) {
      setModuleOff(true)
      setFailure(null)
      return
    }
    setFailure(isApiError(error) ? error.message : fallback)
  }

  const issue = useMutation({
    mutationFn: async (refresh: boolean): Promise<ApprovalLinkIssued> => {
      const response = await api.post<{ data: ApprovalLinkIssued }>(
        endpoints.quotationApprovalLink(quotationId),
        { refresh },
      )
      return response.data
    },
    onSuccess: (issued) => {
      setFailure(null)
      setLink(issued)
    },
    onError: (error: unknown) => {
      handleFailure(error, t("quotation.approvalLinkFailed"))
    },
  })

  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * THE PRINTABLE SHEET — FETCHED AS BYTES, NEVER NAVIGATED TO.
   *
   * The PDF route is authenticated with a BEARER token, so an `<a href>` or a
   * `window.open` at the URL sends no Authorization header and lands the API's
   * 401 JSON inside a PDF viewer. `useQuotationPdf` records the same reasoning;
   * this is the same rule applied to the one sheet that carries the QR.
   *
   * THE OBJECT URL IS REVOKED ON UNMOUNT, because `createObjectURL` pins the
   * whole blob in memory until it is — and this card can be opened, closed and
   * reopened all afternoon.
   */
  const sheetUrlRef = React.useRef<string | null>(null)
  React.useEffect(
    () => () => {
      if (sheetUrlRef.current !== null) URL.revokeObjectURL(sheetUrlRef.current)
    },
    [],
  )

  const sheet = useMutation({
    mutationFn: async () =>
      requestBlob(endpoints.quotationApprovalSheet(quotationId), {
        fallbackFilename: `quotation-${String(quotationId)}-approval.pdf`,
      }),
    onSuccess: (result) => {
      setFailure(null)
      if (sheetUrlRef.current !== null) URL.revokeObjectURL(sheetUrlRef.current)
      const next = URL.createObjectURL(result.blob)
      sheetUrlRef.current = next
      // A new tab rather than a download: he is about to PRINT it.
      window.open(next, "_blank", "noopener,noreferrer")
    },
    onError: (error: unknown) => {
      handleFailure(error, t("quotation.approvalSheetFailed"))
    },
  })

  if (!can("quotation.share")) return null

  /**
   * COPY, WITH A FALLBACK THAT IS NOT A SILENT FAILURE.
   *
   * `navigator.clipboard` needs a secure context and a user gesture, and on an
   * older Android WebView inside the Capacitor shell it may simply not be there.
   * The link is in a read-only input beside the button precisely so that when
   * the copy cannot happen, the owner can still select it by hand — a control
   * that appears to work and does nothing is worse than one that hands you the
   * text.
   */
  async function copyLink(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success(t("quotation.approvalCopied"))
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setFailure(t("quotation.approvalLinkFailed"))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("quotation.approvalLinkTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        {/* Where it stands: sent when, answered when. Both come from the
            document, so this says what the SERVER recorded and never what this
            screen assumed. */}
        {sentAt !== null && (
          /*
            ═══════════════════════════════════════════════════════════════════
            HOW IT WENT OUT, NOT JUST WHEN — and this line is why deleting the
            two "record as sent" buttons was allowed to happen at all.

            `sent_channel` exists to answer a customer who says they never
            received the quotation: "we emailed it on the 4th" and "he says he
            told them on the 4th" are different answers and only one of them can
            be checked. With the buttons that wrote it gone, a column nothing
            DISPLAYS is a column that quietly becomes decorative and is deleted
            by somebody a year from now who cannot see what it is for.

            Two keys rather than one interpolated blank: a quotation stamped
            before migration 19, or through a path that captured no channel,
            reads as a plain date rather than as "sent on 4 Aug by ."
            ═══════════════════════════════════════════════════════════════════
          */
          <p className="text-muted-foreground text-sm">
            {sentChannel === null
              ? t("quotation.sentOn", { date: formatDay(sentAt.slice(0, 10)) })
              : t("quotation.sentOnVia", {
                  date: formatDay(sentAt.slice(0, 10)),
                  channel: t(`quotation.sendChannel.${sentChannel}`),
                })}
            {decidedAt === null ? ` — ${t("quotation.waitingForReply")}` : ""}
          </p>
        )}
        {decidedAt !== null && (
          <div className="flex w-full flex-col gap-1">
            <p className="text-muted-foreground text-sm">
              {/* The NAME when there is one, and the bare date when there is
                  not — a quotation answered before these columns existed, or
                  through a path that never captured one. Two keys rather than an
                  interpolated blank, so neither sentence reads as a mistake. */}
              {decidedByName === null
                ? t("quotation.approvalDecided", { date: formatDay(decidedAt.slice(0, 10)) })
                : t("quotation.approvalDecidedBy", {
                    date: formatDay(decidedAt.slice(0, 10)),
                    name: decidedByName,
                  })}
            </p>
            {decisionNote !== null && (
              <div className="bg-muted/50 w-full rounded-md border p-2">
                <p className="text-muted-foreground text-xs font-medium">
                  {t("quotation.approvalNoteTitle")}
                </p>
                {/*
                  `whitespace-pre-wrap`: the customer typed this into a textarea
                  and their line breaks are part of what they meant. `break-words`
                  because a pasted part number with no spaces would otherwise
                  push the card wider than the phone it is read on.
                */}
                <p className="text-sm break-words whitespace-pre-wrap">{decisionNote}</p>
              </div>
            )}
          </div>
        )}

        {/*
          THE FLAG IS OFF — say so in the app's own words and draw no control.
          Everything below this point needs `approval_links`, and both of its
          routes answer 404 without it, so a button here is a button that cannot
          work. The facts above are kept: they come off the document.
        */}
        {/*
          ═══════════════════════════════════════════════════════════════════════
          ANSWERED MEANS THERE IS NOTHING LEFT TO OFFER (2026-08-20).

          Once the customer has decided, the link controls go entirely: no
          issuing, no copying, no sheet to print. Everything they do concerns a
          question that has been answered, and the server refuses all of it
          anyway — `issueApprovalLink` answers 409 and `liveApprovalUrl` answers
          null. A control whose only outcome is a refusal is worse than no
          control: it invites the owner to wonder what he did wrong.

          THE FACTS ABOVE STAY, and they are the whole point of the card now —
          when it went out and how, who answered, and what they wrote.
          ═══════════════════════════════════════════════════════════════════════
        */}
        {decidedAt !== null ? null : moduleOff ? (
          <FeatureOff className="w-full" module={t("quotation.approvalLinkTitle")} />
        ) : (
          <>
        {link === null ? (
          <Button
            variant="outline"
            disabled={issue.isPending}
            onClick={() => {
              issue.mutate(false)
            }}
          >
            <LinkIcon />
            {t("quotation.approvalIssue")}
          </Button>
        ) : (
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-wrap items-center gap-2">
              {/* READ-ONLY, and shown in full. The owner is about to paste this
                  into a message; a truncated preview is a link he cannot check. */}
              <Input
                readOnly
                value={link.url}
                onFocus={(event) => event.currentTarget.select()}
                className="min-w-0 flex-1 font-mono text-xs"
                aria-label={t("quotation.approvalLinkTitle")}
              />
              <Button variant="secondary" onClick={() => void copyLink(link.url)}>
                {copied ? <CheckIcon /> : <CopyIcon />}
                {t("btn.copy")}
              </Button>
            </div>

            <p className="text-muted-foreground text-xs">
              {t("quotation.approvalValidUntil", {
                date: formatDay(link.expiresAt.slice(0, 10)),
              })}
            </p>

            {/*
              ═══════════════════════════════════════════════════════════════════
              THE THIRD DOOR, NOW A CONTROL RATHER THAN A SENTENCE.

              This used to read "the QR is printed on the sheet" and leave the
              owner to work out which sheet. It was also not quite true any more:
              since the code became opt-in, an ordinary download has no QR at all,
              so the sentence described a document he could not get.

              The button fetches the SAME PDF route with `?approvalQr=true` — one
              renderer, one document, one option — and hands it to the browser to
              save or print.
              ═══════════════════════════════════════════════════════════════════
            */}
            <Button
              variant="outline"
              size="sm"
              disabled={sheet.isPending}
              onClick={() => {
                sheet.mutate()
              }}
            >
              <QrCodeIcon />
              {t("quotation.approvalSheet")}
            </Button>
            <p className="text-muted-foreground text-xs">{t("quotation.approvalSheetHelp")}</p>

            <Button
              variant="ghost"
              size="sm"
              className="self-start"
              disabled={issue.isPending}
              onClick={() => {
                issue.mutate(true)
              }}
            >
              {t("quotation.approvalNewLink")}
            </Button>
          </div>
        )}

        {/*
          ═══════════════════════════════════════════════════════════════════════
          "RECORD AS SENT" USED TO BE TWO BUTTONS HERE, AND IS NOW NONE.

          The owner asked for one control on 2026-08-20: "we already have mark as
          sent, so remove sent on whatsapp and record as sent". The survivor is
          "Mark as sent" in the STATUS card above, and that was not a free
          deletion — those two buttons posted a CHANNEL and the status button did
          not, so removing them naively would have thrown away both `sent_at`
          (which §11.2's "waiting for reply" ageing is measured from) and the
          record of HOW an offer went out.

          So the channel moved onto the surviving control:
          `POST /quotations/:id/status` now carries it and stamps both columns.
          Nothing was lost, and this card shows what was recorded in the line at
          the top rather than offering a second way to write it.
          ═══════════════════════════════════════════════════════════════════════
        */}
          </>
        )}

        {failure !== null && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
